import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import { z } from 'zod'

extendZodWithOpenApi(z)

export const BecoolWebhookEventType = z
  .enum(['delivery_completed', 'delivery_scheduled', 'delivery_exception', 'unknown_event_received', 'out_for_delivery', 'received_at_origin_facility'])
  .describe('Becool event type')
export type BecoolWebhookEventType = z.infer<typeof BecoolWebhookEventType>

// Box-level webhooks (one per box) — as opposed to order-level (delivery_scheduled / delivery_completed).
// Skipped in both customer notifications and Laravel forwarding for the moment
export const BoxLevelBecoolEventTypes = [
  BecoolWebhookEventType.enum.out_for_delivery,
  BecoolWebhookEventType.enum.received_at_origin_facility,
] as const satisfies readonly BecoolWebhookEventType[]
export type BoxLevelBecoolEventType = (typeof BoxLevelBecoolEventTypes)[number]

// Type predicate so callers can narrow `BecoolWebhookEventType` without an unsafe cast.
export function isBoxLevelBecoolEvent(name: BecoolWebhookEventType): name is BoxLevelBecoolEventType {
  return (BoxLevelBecoolEventTypes as readonly BecoolWebhookEventType[]).includes(name)
}

export const BaseBecoolWebhookEvent = z
  .object({
    type: z.string().optional().describe('Becool Event Type'),
    payload: z
      .object({
        tracking: z.string().describe('Courier tracking number'),
        invoice: z.string().regex(/^(LYKA|TYGA)/).describe('Invoice number e.g. LYKAP001231001 or TYGA001231001'),
        ticket: z.string().optional().nullable(),
      })
      .passthrough(),
  })
  .passthrough()
export type BaseBecoolWebhookEvent = z.infer<typeof BaseBecoolWebhookEvent>

export const BecoolDeliveryCompletedEvent = BaseBecoolWebhookEvent
  .extend({
    type: z.literal(BecoolWebhookEventType.enum.delivery_completed),
    payload: BaseBecoolWebhookEvent.shape.payload.extend({
      time: z.string().nullable().describe('Time of delivery (completed) in YYYY-MM-DD HH:mm:ss format'),
      latitude: z.number().nullable().describe('Latitude of delivery location'),
      longitude: z.number().nullable().describe('Longitude of delivery location'),
      signed_by: z.string().nullable().describe('Name of person who signed for the delivery'),
    }),
  })
  .strip()
export type BecoolDeliveryCompletedEvent = z.infer<typeof BecoolDeliveryCompletedEvent>

// BeCool depot object embedded in `received_at_origin_facility` payloads.
// `depot_code` is kept as a free string (rather than the stricter BeCoolDepotCode enum) so
// new BeCool depots don't break webhook ingestion. Named with the Webhook prefix to avoid
// collision with the courier service's TypeORM BecoolDepot entity.
export const BecoolWebhookDepot = z.object({
  id: z.number(),
  region_id: z.number(),
  region_name: z.string(),
  name: z.string(),
  address: z.string(),
  address2: z.string().nullable(),
  suburb: z.string(),
  state: z.string(),
  postcode: z.string(),
  depot_code: z.string().describe('BeCool depot code, e.g. SYD, MEL, HBA'),
  wms: z.boolean(),
})
export type BecoolWebhookDepot = z.infer<typeof BecoolWebhookDepot>

export const BecoolOutForDeliveryEvent = BaseBecoolWebhookEvent
  .extend({
    type: z.literal(BecoolWebhookEventType.enum.out_for_delivery),
    payload: BaseBecoolWebhookEvent.shape.payload.extend({
      time: z.string().describe('Date left facility in YYYY-MM-DD format'),
      box_serial: z.string().describe('Box serial number'),
      depot: z.string().describe('Depot name, e.g. "Hobart" (not the 3-letter depot code)'),
    }),
  })
  .strip()
export type BecoolOutForDeliveryEvent = z.infer<typeof BecoolOutForDeliveryEvent>

export const BecoolReceivedAtOriginFacilityEvent = BaseBecoolWebhookEvent
  .extend({
    type: z.literal(BecoolWebhookEventType.enum.received_at_origin_facility),
    payload: BaseBecoolWebhookEvent.shape.payload.extend({
      time: z.string().describe('Time of receipt at origin facility in YYYY-MM-DD format'),
      box_serial: z.string().describe('Box serial number'),
      depot: BecoolWebhookDepot.describe('Origin facility depot details'),
    }),
  })
  .strip()
export type BecoolReceivedAtOriginFacilityEvent = z.infer<typeof BecoolReceivedAtOriginFacilityEvent>

export const BecoolDeliveryScheduledEvent = BaseBecoolWebhookEvent
  .extend({
    type: z.literal(BecoolWebhookEventType.enum.delivery_scheduled),
    payload: BaseBecoolWebhookEvent.shape.payload.extend({
      date: z.string().describe('Date of delivery (scheduled) in YYYY-MM-DD format'),
      eta: z.string().describe('Estimated time of delivery (scheduled) in YYYY-MM-DD HH:mm:ss format'),
    }),
  })
  .strip()
export type BecoolDeliveryScheduledEvent = z.infer<typeof BecoolDeliveryScheduledEvent>

export const BecoolErrorCode = z
  .enum(['CNX', 'R', 'RREC', 'GNR', 'G', 'U', 'DG', 'BC', 'BA', 'A', 'I', 'S', 'D', 'BT', 'ORC', 'O'])
  .describe('Becool error reson code.')
export type BecoolErrorCode = z.infer<typeof BecoolErrorCode>

export const BecoolDeliveryExceptionEvent = BaseBecoolWebhookEvent
  .extend({
    type: z.literal(BecoolWebhookEventType.enum.delivery_exception),
    payload: BaseBecoolWebhookEvent.shape.payload.extend({
      status: z.string().describe('Shipment status'),
      time: z.string().nullable().describe('Error event time in YYYY-MM-DD HH:mm:ss format'),
      reason_code: z.string().nullable().describe('Becool error reason code'),
    }),
  })
  .strip()
export type BecoolDeliveryExceptionEvent = z.infer<typeof BecoolDeliveryExceptionEvent>
